import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import useMatchMedia from 'hooks/useMatchMedia';
import { Button, Overlay } from 'ncoded-component-library';
import Portal from 'components/Portal';
import { Position } from 'models/Position';
import { ObjectValues, XOR } from 'types';
import XIcon from 'icons/X.icon';
import useFloatingBannerPositioning from './hooks/useFloatingBannerPositioning';

import './FloatingBanner.styles.scss';

export const FLOATING_BANBER_COLORS = {
  GREEN: 'green',
  BLACK: 'black',
  WHITE: 'white',
};

type FlaotingBannerColors = ObjectValues<typeof FLOATING_BANBER_COLORS>;

export type FloatingBannerProps = {
  className?: string;
  color?: FlaotingBannerColors;
  preferredPosition?: Position;
  showOnPhablet?: boolean;
  hasCloseButton?: boolean;
  hasOverlay?: boolean;
  onClose?: () => void;
} & XOR<{ anchor: ReactNode }, { selector: string }> &
  XOR<{ onHover: boolean }, { showBannerNotification?: boolean }>;

const FloatingBanner: React.FC<FloatingBannerProps> = (props) => {
  const {
    className,
    children,
    anchor,
    selector,
    showBannerNotification,
    onHover,
    showOnPhablet,
    hasOverlay,
    hasCloseButton = true,
    color = FLOATING_BANBER_COLORS.GREEN,
    preferredPosition,
    onClose,
  } = props;

  const isPhablet = useMatchMedia('isPhablet');
  const showBanner = showBannerNotification && (showOnPhablet || !isPhablet);

  const [isInViewport, setIsInViewport] = useState(onHover || showBanner);
  const [isVisible, setIsVisible] = useState(showBanner);

  const {
    position,
    bannerPosition,
    anchorRef,
    contentRef,
    updateBannerPosition,
  } = useFloatingBannerPositioning({
    onHover,
    preferredPosition,
    setIsInViewport,
  });

  const classes = classNames(
    'floating-banner',
    color,
    bannerPosition.current,
    className,
  );

  const onMouseEnter = useCallback(() => setIsVisible(true), []);
  const onMouseLeave = useCallback(() => setIsVisible(false), []);

  const setAnchorRef = useCallback(() => {
    if (!selector) return;

    const el = document.querySelector(selector) as HTMLDivElement;

    if (!el) return;

    anchorRef.current = el;

    if (!onHover) return;

    el.classList.add('clickable');
    el.addEventListener('mouseenter', onMouseEnter);
    el.addEventListener('mouseleave', onMouseLeave);

    return () => {
      el.removeEventListener('mouseenter', onMouseEnter);
      el.removeEventListener('mouseleave', onMouseLeave);
    };
  }, [anchorRef, selector, onHover, onMouseEnter, onMouseLeave]);

  useEffect(() => {
    const cleanup = setAnchorRef();

    return () => {
      cleanup?.();
    };
  }, [setAnchorRef]);

  useEffect(() => {
    if (isVisible) {
      updateBannerPosition();

      window.addEventListener('scroll', updateBannerPosition, true);
      window.addEventListener('resize', updateBannerPosition, true);

      return () => {
        window.removeEventListener('scroll', updateBannerPosition, true);
        window.removeEventListener('resize', updateBannerPosition, true);
      };
    }
  }, [isVisible, anchor, selector, setAnchorRef, updateBannerPosition]);

  useEffect(() => {
    if (onHover) return;

    setIsVisible(showBannerNotification);
  }, [onHover, showBannerNotification]);

  return (
    <>
      {anchor && (
        <div
          className={classNames('floating-banner__anchor', {
            clickable: onHover,
          })}
          ref={anchorRef}
          {...(onHover && {
            onMouseEnter,
            onMouseLeave,
          })}
        >
          {anchor}
        </div>
      )}

      {hasOverlay && !onHover && (
        <Overlay
          background="transparent"
          className="floating-banner__overlay"
          open
          noContent
          overlayCloses
          lockScroll
          onClose={onClose}
        />
      )}

      <Portal isOpen={isInViewport && isVisible}>
        <div className={classes} style={position} ref={contentRef}>
          {children}

          {hasCloseButton && (
            <Button
              variant="icon"
              className="floating-banner__close"
              icon={XIcon}
              onClick={(e) => {
                e.stopPropagation();
                setIsInViewport(false);
                onClose?.();
              }}
            />
          )}
        </div>
      </Portal>
    </>
  );
};

export default FloatingBanner;
